import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { secureStorage, inputValidation } from '@/lib/security-utils';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { SessionTimeoutWarning } from '@/components/security/SessionTimeoutWarning';

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  is_active: boolean;
}

interface Company {
  id: string;
  name: string;
  nit: string | null;
  logo_url: string | null;
  country: string;
  currency: string;
  menu_config?: any;
}

interface UserCompany {
  id: string;
  user_id: string;
  company_id: string;
  role: string;
  is_active: boolean;
  company: Company;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  userCompanies: UserCompany[];
  selectedCompany: Company | null;
  loading: boolean;
  isInitialized: boolean; // ✅ Agregar esta propiedad
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  selectCompany: (company: Company) => void;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [userCompanies, setUserCompanies] = useState<UserCompany[]>([]);
  const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // 🔒 FUNCIÓN CONTROLADA para cambiar empresa con logging completo
  const setSelectedCompany = (company: Company | null) => {
    const stack = new Error().stack;
    console.log('🏢 CAMBIO DE EMPRESA DETECTADO:', {
      anterior: selectedCompany?.name || 'null',
      nueva: company?.name || 'null',
      stack: stack?.split('\n').slice(1, 4).map(line => line.trim())
    });
    setSelectedCompanyState(company);
  };

  const signOut = async () => {
    console.log('🚪 Cerrando sesión del usuario:', user?.id);
    
    // 🔒 SEGURIDAD CRÍTICA: Limpiar TODOS los datos al cerrar sesión
    try {
      // Limpiar datos específicos del usuario si existe
      if (user?.id) {
        secureStorage.removeItem(`lastCompany_${user.id}`);
      }
      
      // Limpiar TODOS los datos de empresa sin excepción
      secureStorage.removeItem('selectedCompanyData');
      secureStorage.removeItem('selectedCompanyId');
      secureStorage.removeItem('lastCompanyId');
      
      // Limpiar cualquier dato de empresa que pueda existir
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('Company') || key.includes('company') || key.includes('lastCompany')) {
          localStorage.removeItem(key);
        }
      });
      
      // Usar el método de limpieza segura completa
      secureStorage.clearSensitiveData();
      
      // 🧹 CRÍTICO: Limpiar sessionStorage completamente
      try {
        sessionStorage.clear();
        console.log('🧹 sessionStorage limpiado completamente');
      } catch (sessionError) {
        console.error('Error limpiando sessionStorage:', sessionError);
      }
      
      console.log('🧹 TODOS los datos de empresa limpiados completamente');
    } catch (error) {
      console.error('Error limpiando localStorage:', error);
      // Fallback: limpiar localStorage completo si hay error
      try {
        localStorage.clear();
        sessionStorage.clear();
        console.log('🧹 localStorage y sessionStorage limpiados completamente como fallback');
      } catch (clearError) {
        console.error('Error en limpieza fallback:', clearError);
      }
    }
    
    // Limpiar estado antes del signOut para evitar condiciones de carrera
    setSelectedCompany(null);
    setUserCompanies([]);
    setProfile(null);
    
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    
    // Segunda limpieza después del signOut para asegurar estado limpio
    setSelectedCompany(null);
    setUserCompanies([]);
    setProfile(null);
    
    console.log('✅ SignOut completado con limpieza total de datos');
    
    // 🔄 Forzar recarga completa de la página para reiniciar toda la aplicación
    // Esto asegura que todos los estados, caches y suscripciones se reinicien
    setTimeout(() => {
      window.location.href = '/auth';
    }, 100);
  };

  // Session timeout management - moved after signOut declaration
  const { showWarning, timeLeft, extendSession } = useSessionTimeout(
    {
      timeoutMinutes: 480, // 8 horas para POS
      warningMinutes: 15,  // Advertencia 15 minutos antes
      enabled: true
    },
    user,
    signOut
  );

  // 🔒 FUNCIÓN SIMPLIFICADA Y ROBUSTA para restaurar empresa
  const restoreSelectedCompany = async (userId: string, availableCompanies: UserCompany[]) => {
    console.log('🔄 INICIANDO restauración de empresa para usuario:', userId);
    
    if (!availableCompanies || availableCompanies.length === 0) {
      console.log('⚠️ No hay empresas disponibles para restaurar');
      return false;
    }

    try {
      // 1. Buscar empresa guardada en secureStorage específica del usuario
      const savedCompanyId = secureStorage.getItem(`lastCompany_${userId}`);
      console.log('📦 Empresa guardada encontrada:', savedCompanyId);
      
      if (savedCompanyId) {
        // 2. Verificar que la empresa existe en las empresas disponibles
        const foundCompany = availableCompanies.find(uc => uc.company.id === savedCompanyId);
        
        if (foundCompany) {
          console.log('✅ RESTAURANDO empresa:', foundCompany.company.name);
          setSelectedCompany(foundCompany.company);
          
          // 3. Guardar nuevamente para refrescar timestamp (usando secureStorage)
          secureStorage.setItem(`lastCompany_${userId}`, foundCompany.company.id);
          secureStorage.setItem('currentCompanyId', foundCompany.company.id);
          secureStorage.setItem('currentCompanyName', foundCompany.company.name);
          
          return true;
        } else {
          console.warn('⚠️ Empresa guardada no disponible, limpiando...');
          secureStorage.removeItem(`lastCompany_${userId}`);
          secureStorage.removeItem('currentCompanyId');
          secureStorage.removeItem('currentCompanyName');
        }
      }
      
      return false;
    } catch (error) {
      console.error('❌ Error en restauración:', error);
      return false;
    }
  };

  // Función para limpiar datos inválidos de localStorage
  const cleanupInvalidCompanyData = (userId: string) => {
    try {
      secureStorage.removeItem(`lastCompany_${userId}`);
      secureStorage.removeItem('selectedCompanyData');
      secureStorage.removeItem('selectedCompanyId');
      console.log('🧹 Limpieza de datos inválidos completada para usuario:', userId);
    } catch (error) {
      console.error('Error limpiando datos inválidos:', error);
    }
  };

  const fetchUserData = async (userId: string) => {
    // 🔒 VALIDACIÓN CRÍTICA: Verificar que userId sea válido
    if (!userId || userId === 'undefined' || userId === 'null') {
      console.error('❌ CRITICAL: Invalid userId provided to fetchUserData:', userId);
      return;
    }

    try {
      // Cargar perfil básico
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (profileError) {
          console.error('Error loading profile:', profileError);
          // No fallar completamente, continuar con el flujo
        } else if (profileData) {
          setProfile(profileData);
        } else {
          console.warn('⚠️ No profile found for user:', userId);
        }
      } catch (error) {
        console.warn('Profile not found:', error);
      }

      // Intentar cargar empresas reales del usuario usando user_roles
      try {
        console.log('🏢 Cargando empresas del usuario:', userId);
        
        // Verificar si el usuario es Super Admin
        const { data: superAdminCheck, error: superAdminError } = await supabase
          .rpc('is_user_super_admin', { _user_id: userId });
        
        if (superAdminError) {
          console.error('Error checking super admin status:', superAdminError);
          // Continuar como usuario regular si hay error
        }
        
        const isSuperAdmin = superAdminCheck === true;
        console.log('🔐 Usuario es Super Admin:', isSuperAdmin);
        
        let userRolesData;
        let rolesError;
        
        if (isSuperAdmin) {
          // Super Admin: Cargar TODAS las empresas del sistema
          console.log('🌟 Cargando TODAS las empresas para Super Admin');
          const { data: allCompanies, error: companiesError } = await supabase
            .from('companies')
            .select('*, menu_config')
            .eq('is_active', true)
            .order('name');
          
          if (!companiesError && allCompanies) {
            // Mapear al formato de user_roles
            userRolesData = allCompanies.map(company => ({
              id: `super-admin-${company.id}`,
              user_id: userId,
              company_id: company.id,
              is_active: true,
              company: company
            }));
          }
          rolesError = companiesError;
        } else {
          // Usuario regular: Cargar solo sus empresas asignadas
          const { data, error } = await supabase
            .from('user_roles')
            .select(`
              *,
              company:companies(*, menu_config)
            `)
            .eq('user_id', userId)
            .eq('is_active', true);
          
          userRolesData = data;
          rolesError = error;
        }

        console.log('🏢 Respuesta de empresas:', { 
          count: userRolesData?.length || 0, 
          error: rolesError,
          companies: userRolesData?.map(r => r.company?.name) || [],
          isSuperAdmin
        });

        if (!rolesError && userRolesData && userRolesData.length > 0) {
          console.log('🏢 Mapeando empresas del usuario...');
          
          // Deduplicar empresas por company_id para evitar duplicados
          const uniqueCompanies = new Map();
          userRolesData.forEach(role => {
            if (role.company && !uniqueCompanies.has(role.company.id)) {
              uniqueCompanies.set(role.company.id, {
                id: role.id,
                user_id: role.user_id,
                company_id: role.company_id,
                role: 'admin',
                is_active: role.is_active,
                company: role.company
              });
            }
          });

          const userCompanies = Array.from(uniqueCompanies.values());
          
          console.log('🏢 Empresas únicas mapeadas:', {
            total: userCompanies.length,
            companies: userCompanies.map(uc => uc.company.name)
          });
          
          setUserCompanies(userCompanies);
          
          // 🔒 RESTAURACIÓN DIRECTA Y ROBUSTA de empresa
          const companyRestored = await restoreSelectedCompany(userId, userCompanies);
          
          // Si no se pudo restaurar, seleccionar la primera empresa disponible
          if (!companyRestored && userCompanies.length > 0) {
            console.log('🔄 Seleccionando primera empresa como fallback:', userCompanies[0].company.name);
            
            const firstCompany = userCompanies[0].company;
            setSelectedCompany(firstCompany);
            
            // Guardar inmediatamente esta selección (usando secureStorage)
            secureStorage.setItem(`lastCompany_${userId}`, firstCompany.id);
            secureStorage.setItem('currentCompanyId', firstCompany.id);
            secureStorage.setItem('currentCompanyName', firstCompany.name);
            
            console.log('💾 Empresa fallback guardada');
          }
          
          return; // Salir exitosamente con datos reales
        }
      } catch (error) {
        console.warn('Failed to fetch user roles:', error);
      }

      // 🔒 SEGURIDAD: Fallback seguro - crear SOLO empresa demo específica para usuarios sin asignación
      console.log('⚠️ Usuario sin roles asignados - creando empresa demo segura');
      try {
        // Crear empresa demo específica para este usuario
        const demoCompanyData = {
          name: 'Empresa Demo - Usuario Nuevo',
          nit: null,
          country: 'GT',
          currency: 'GTQ',
          is_active: true,
          is_demo: true, // Marcar como empresa demo
        };

        const { data: demoCompany, error: createCompanyError } = await supabase
          .from('companies')
          .insert(demoCompanyData)
          .select()
          .single();

        if (!createCompanyError && demoCompany) {
          console.log('✅ Empresa demo creada exitosamente:', demoCompany.name);
          
          // Asignar usuario SOLO a la empresa demo creada con rol básico
          const { data: adminRole } = await supabase
            .from('roles')
            .select('id')
            .eq('code', 'admin')
            .single();

          if (adminRole) {
            const { error: roleError } = await supabase
              .from('user_roles')
              .insert({
                user_id: userId,
                company_id: demoCompany.id,
                role_id: adminRole.id,
                is_active: true,
                assigned_by: userId
              });

            if (!roleError) {
              // Crear relación en user_companies también
              await supabase
                .from('user_companies')
                .insert({
                  user_id: userId,
                  company_id: demoCompany.id,
                  role: 'admin',
                  is_active: true
                });

              // Establecer en el estado local
              const userCompaniesWithDemo = [{
                id: `demo-role-${userId}-${demoCompany.id}`,
                user_id: userId,
                company_id: demoCompany.id,
                role: 'admin',
                is_active: true,
                company: demoCompany
              }];

              setUserCompanies(userCompaniesWithDemo);
              setSelectedCompany(demoCompany);
              console.log('✅ Usuario asignado exitosamente a empresa demo');
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error creando empresa demo segura:', error);
      }

      // Solo como último recurso, crear empresa demo
      console.log('Using demo company as last resort');
      const demoCompany: Company = {
        id: '550e8400-e29b-41d4-a716-446655440000',
        name: 'Empresa Demo',
        nit: '12345678-9',
        logo_url: null,
        country: 'GT',
        currency: 'GTQ'
      };

      setUserCompanies([{
        id: '550e8400-e29b-41d4-a716-446655440001',
        user_id: userId,
        company_id: demoCompany.id,
        role: 'admin',
        is_active: true,
        company: demoCompany
      }]);

      if (!selectedCompany) {
        setSelectedCompany(demoCompany);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Función para rastrear sesión de usuario - OPTIMIZADA CON MEJOR DEBOUNCE
  const trackUserSession = useMemo(() => {
    let lastTrackTime = 0;
    const TRACK_INTERVAL = 60000; // Aumentado a 60 segundos mínimo
    let pendingTrack: NodeJS.Timeout | null = null;
    
    return async (userId: string) => {
      const now = Date.now();
      
      // Evitar llamadas muy frecuentes
      if (now - lastTrackTime < TRACK_INTERVAL) {
        console.log('⏭️ Skipping session track - too soon');
        return;
      }
      
      // Cancelar track pendiente
      if (pendingTrack) {
        clearTimeout(pendingTrack);
      }
      
      // Debounce para evitar múltiples calls
      pendingTrack = setTimeout(async () => {
        lastTrackTime = Date.now();
        
        const userAgent = navigator.userAgent || 'Unknown';
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
        
        console.log('📊 Tracking user session...');
        
        // Usar cliente centralizado con retry automático
        const { trackUserSession } = await import('@/utils/edgeFunctionClient');
        await trackUserSession({
          user_agent: userAgent,
          timezone: timezone,
          branch_id: null
        });
        
        console.log('✅ Session tracked successfully');
        pendingTrack = null;
      }, 2000); // 2 segundos de debounce
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // 🔍 NUEVA: Validar y reparar localStorage al inicio
        try {
          secureStorage.validateAndRepairStorage();
        } catch (storageError) {
          console.warn('Error validando localStorage, continuando...', storageError);
        }
        
        // Get initial session with error handling for invalid refresh tokens
        const { data: { session }, error } = await supabase.auth.getSession();
        
        // Handle refresh token errors specifically
        if (error && (
          error.message?.includes('refresh_token_not_found') ||
          error.message?.includes('Invalid Refresh Token') ||
          error.message?.includes('refresh token not found')
        )) {
          console.warn('🚨 Invalid refresh token detected, clearing auth data...');
          
          // Clear all authentication data
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('sb-xhmufnednujfyxmcznuy-auth-token');
          
          // Clear any other Supabase keys
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.includes('supabase') || key.includes('sb-')) {
              localStorage.removeItem(key);
            }
          });
          
          // Sign out to clear any remaining session data
          await supabase.auth.signOut();
          
          console.log('✅ Auth data cleared, user needs to re-authenticate');
          
          // Set clean state
          setSession(null);
          setUser(null);
          setProfile(null);
          setUserCompanies([]);
          setSelectedCompany(null);
          setLoading(false);
          setIsInitialized(true);
          return;
        }
        
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          
          if (session?.user) {
            await fetchUserData(session.user.id);
            // Rastrear la sesión después de establecer los datos del usuario
            await trackUserSession(session.user.id);
          }
          // Always set loading as false AND initialized as true
          setLoading(false);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Error getting session:', error);
        if (mounted) {
          // Even if there's an error, set loading as false and initialized as true
          setLoading(false);
          setIsInitialized(true);
        }
      }
    };

    // Setup auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('Auth state changed:', event, session?.user?.id);
        
        // Forzar flujo de recuperación de contraseña si viene del enlace de Supabase
        if (event === 'PASSWORD_RECOVERY') {
          try {
            localStorage.setItem('password_recovery_mode', 'true');
          } catch {}
          const search = typeof window !== 'undefined' ? window.location.search : '';
          const hash = typeof window !== 'undefined' ? window.location.hash : '';
          if (typeof window !== 'undefined' && window.location.pathname !== '/reset-password') {
            console.log('🔒 PASSWORD_RECOVERY detectado - redirigiendo a /reset-password preservando tokens');
            window.location.replace(`/reset-password${search}${hash}`);
            return; // Evitar procesamiento adicional
          }
        }
        
        // Handle auth errors specifically
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('🚨 Token refresh failed, clearing auth data...');
          
          // Clear authentication data on refresh failure
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('sb-xhmufnednujfyxmcznuy-auth-token');
          
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.includes('supabase') || key.includes('sb-')) {
              localStorage.removeItem(key);
            }
          });
          
          setSession(null);
          setUser(null);
          setProfile(null);
          setUserCompanies([]);
          setSelectedCompany(null);
          return;
        }
        
        // Only synchronous state updates here
        setSession(session);
        setUser(session?.user ?? null);

        // Defer async operations to prevent React deadlock
        if (session?.user) {
          // 🔒 VALIDACIÓN CRÍTICA: Verificar que user.id sea válido antes de hacer consultas
          if (!session.user.id || session.user.id === 'undefined') {
            console.error('❌ CRITICAL: Session user ID is invalid:', session.user.id);
            // Forzar cierre de sesión si el ID no es válido (sin await para evitar bloqueo)
            supabase.auth.signOut();
            return;
          }

          setTimeout(() => {
            if (mounted) {
              fetchUserData(session.user.id);
              // Rastrear sesión en eventos de login
              if (event === 'SIGNED_IN') {
                trackUserSession(session.user.id);
              }
            }
          }, 0);
        } else {
          // 🔒 SEGURIDAD CRÍTICA: Cuando no hay sesión, limpiar TODO inmediatamente
          console.log('🧹 Limpiando datos de empresa - no hay sesión válida');
          setProfile(null);
          setUserCompanies([]);
          setSelectedCompany(null);
          
          // Limpiar también localStorage por seguridad
          try {
            secureStorage.removeItem('selectedCompanyData');
            secureStorage.removeItem('selectedCompanyId');
            secureStorage.removeItem('lastCompanyId');
            secureStorage.clearSensitiveData();
            
            // Limpiar cualquier clave relacionada con empresas
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
              if (key.includes('Company') || key.includes('company') || key.includes('lastCompany')) {
                localStorage.removeItem(key);
              }
            });
            
            console.log('✅ Datos de empresa limpiados en onAuthStateChange');
          } catch (error) {
            console.error('Error limpiando datos en onAuthStateChange:', error);
          }
        }
        setLoading(false);
        setIsInitialized(true);
      }
    );

    initAuth();

    // Timeout de seguridad para asegurar que loading no se quede en true
    const loadingTimeout = setTimeout(() => {
      if (mounted) {
        console.info('✅ Inicialización de autenticación completada');
        setLoading(false);
        setIsInitialized(true);
      }
    }, 3000); // ✅ Reducido a 3 segundos para respuesta más rápida

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Si el login fue exitoso, verificar si el usuario está activo y si requiere cambio de contraseña
    if (!error && data.user) {
      try {
        // 🔒 VALIDACIÓN CRÍTICA: Verificar que user.id sea válido
        if (!data.user.id) {
          console.error('❌ CRITICAL: User ID is undefined after successful login');
          await supabase.auth.signOut();
          return {
            error: new Error('Error crítico de autenticación. Por favor, contacte al soporte técnico.')
          };
        }

        console.log('✅ Login exitoso para usuario:', data.user.id);

        // Verificar el estado is_active y require_password_change del perfil del usuario
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_active, full_name, email, require_password_change')
          .eq('user_id', data.user.id)
          .maybeSingle(); // Cambiado a maybeSingle para evitar error si no existe

        if (profileError) {
          console.error('Error checking user profile:', profileError);
          // Cerrar sesión si no se puede verificar el perfil
          await supabase.auth.signOut();
          return { 
            error: new Error('No se pudo verificar el estado del usuario. Contacte al administrador.') 
          };
        }

        // Si no existe el perfil, crear uno básico
        if (!profileData) {
          console.warn('⚠️ No existe perfil para este usuario, creando uno básico...');
          const { error: createError } = await supabase
            .from('profiles')
            .insert({
              user_id: data.user.id,
              email: data.user.email,
              is_active: true,
              require_password_change: false
            });

          if (createError) {
            console.error('Error creating profile:', createError);
            await supabase.auth.signOut();
            return {
              error: new Error('Error al crear el perfil de usuario. Contacte al administrador.')
            };
          }

          // Continuar con el login
          return { error: null };
        }

        // Si el usuario está desactivado, rechazar el login
        if (!profileData.is_active) {
          console.warn('Login rejected - user is deactivated:', data.user.id);
          
          // Cerrar sesión inmediatamente
          await supabase.auth.signOut();
          
          return { 
            error: new Error('Tu cuenta ha sido desactivada. Contacta al administrador para reactivarla.') 
          };
        }

        // 🔒 Si el usuario requiere cambio de contraseña, marcar en sessionStorage
        if (profileData.require_password_change) {
          console.log('⚠️ Usuario requiere cambio de contraseña:', profileData.email);
          sessionStorage.setItem('require_password_change', 'true');
          sessionStorage.setItem('change_password_user_id', data.user.id);
        }

        console.log('✅ Login successful - user is active:', profileData.full_name || profileData.email);
      } catch (validationError) {
        console.error('Error during login validation:', validationError);
        // En caso de error, cerrar sesión por seguridad
        await supabase.auth.signOut();
        return { 
          error: new Error('Error en la validación del usuario. Inténtalo nuevamente.') 
        };
      }
    }

    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };


  // 🔒 FUNCIÓN SIMPLIFICADA Y ROBUSTA para seleccionar empresa
  const selectCompany = async (company: Company) => {
    console.log('🏢 SELECTCOMPANY LLAMADA:', company.name, 'para usuario:', user?.id);
    
    if (!user?.id) {
      console.error('❌ No hay usuario autenticado');
      return;
    }

    // Validar que la empresa está disponible para el usuario
    const isValidCompany = userCompanies.some(uc => uc.company.id === company.id);
    if (!isValidCompany) {
      console.error('🚨 ACCESO DENEGADO: Empresa no autorizada');
      return;
    }

    // 🔒 ESTA ES LA ÚNICA FUNCIÓN QUE DEBE CAMBIAR LA EMPRESA SELECCIONADA
    console.log('✅ ESTABLECIENDO empresa como seleccionada:', company.name);
    setSelectedCompany(company);
    
    // 🔒 SEGURIDAD: Guardar empresa usando secureStorage (encriptado)
    try {
      secureStorage.setItem(`lastCompany_${user.id}`, company.id);
      secureStorage.setItem('currentCompanyId', company.id);
      secureStorage.setItem('currentCompanyName', company.name);
      
      console.log('💾 EMPRESA GUARDADA correctamente:', {
        userId: user.id,
        companyId: company.id,
        companyName: company.name
      });
    } catch (error) {
      console.error('❌ Error guardando empresa:', error);
    }
  };

  const refreshUserData = async () => {
    if (user?.id) {
      console.log('Refreshing user data after permissions update...');
      await fetchUserData(user.id);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    userCompanies,
    selectedCompany,
    loading,
    isInitialized: isInitialized && !loading && session !== null, // ✅ Mejorar la lógica de inicialización
    signIn,
    signUp,
    signOut,
    selectCompany,
    refreshUserData,
  };

  return (
    <AuthContext.Provider value={value}>
      {isInitialized ? children : (
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      )}
      <SessionTimeoutWarning 
        show={showWarning}
        timeLeft={timeLeft}
        onExtend={extendSession}
      />
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('❌ useAuth hook called outside of AuthProvider context');
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}